import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BedDouble, AlertTriangle, RefreshCw, Settings } from 'lucide-react';
import API from '../../api/axiosInstance';
import { addDaysToLADateKey, formatLADateRangeArrow, buildDateKeys } from '../../utils/laDateUtils';
import { totalCapacity } from '../../constants/roomConfig';
import ConfirmModal from './ConfirmModal';
import ConfigurationList from './ConfigurationList';
import BookingDateRangePicker from './BookingDateRangePicker';
import HotelAvailabilityList from './HotelAvailabilityList';
import GuestPicker from './GuestPicker';
import BookingFailureBanner from './BookingFailureBanner';

const ALLOCATION_OPTIONS = [
  { value: 'required', label: 'Required' },
  { value: 'private', label: 'Private' },
];

/** First existing segment whose [from_date, to_date] range overlaps [fromKey, toKey). */
const findOverlap = (segments, fromKey, toKey) => {
  return segments.find((seg) => {
    const segEndExclusive = addDaysToLADateKey(seg.to_date, 1);
    return fromKey < segEndExclusive && seg.from_date < toKey;
  });
};

/**
 * Book/Edit panel for a stay period, embedded in ParticipantBookingTab's StayPeriodCard.
 * mode="create": date range picker (bounded to the stay period) + live availability search
 *   + per-night POST loop on confirm, with partial-failure retry.
 * mode="edit": unit/dates locked (read-only), allocation type + price editable,
 *   PATCH looped across the matched group's booking_ids.
 * `onDone` is called after the panel should close — it both refetches the parent's
 * grouped bookings and closes the panel, so state always stays in sync on exit.
 */
const ParticipantBookingPanel = ({
  mode,
  stayInKey,
  stayOutKey,
  competitionId,
  participantId,
  group,
  existingSegments = [],
  guests = [],
  onDone,
}) => {
  const isEdit = mode === 'edit';

  const [fromKey, setFromKey] = useState(stayInKey);
  const [toKey, setToKey] = useState(stayOutKey);
  const [availability, setAvailability] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availError, setAvailError] = useState('');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [configModalRoom, setConfigModalRoom] = useState(null);
  const [collapsedHotels, setCollapsedHotels] = useState(new Set());

  const [allocationType, setAllocationType] = useState(
    isEdit ? (group?.allocation_type || 'required') : 'required'
  );
  const [price, setPrice] = useState(isEdit ? String(group?.price ?? '') : '');
  const [selectedGuestIds, setSelectedGuestIds] = useState(
    isEdit ? (group?.selected_guest_ids?.map((id) => String(id)) || []) : []
  );
  const [isGuestOnly, setIsGuestOnly] = useState(isEdit ? (group?.is_guest_only || false) : false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [failedNights, setFailedNights] = useState([]); // [{ dateKey, message }]
  const [pendingDateKeys, setPendingDateKeys] = useState([]);
  const [overlapWarning, setOverlapWarning] = useState(null); // { message } | null

  const dateKeys = useMemo(() => buildDateKeys(fromKey, toKey), [fromKey, toKey]);
  const nights = dateKeys.length;
  const validRange = Boolean(fromKey && toKey && fromKey < toKey);

  const fetchAvailability = useCallback(async () => {
    if (isEdit || !competitionId || !validRange) return;
    setLoadingAvailability(true);
    setAvailError('');
    try {
      const res = await API.get('/ems/room-bookings/availability', {
        params: { competition_id: competitionId, from: fromKey, to: toKey },
      });
      setAvailability(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setAvailError(err.response?.data?.message || 'Failed to load availability');
      setAvailability([]);
    } finally {
      setLoadingAvailability(false);
    }
  }, [isEdit, competitionId, fromKey, toKey, validRange]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  useEffect(() => {
    if (isEdit) return;
    setPendingDateKeys(dateKeys);
    setFailedNights([]);
    setSelectedUnit(null);
    setSubmitError('');
    setCollapsedHotels(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromKey, toKey]);

  const toggleHotelCollapsed = (hotelId) => {
    setCollapsedHotels((prev) => {
      const next = new Set(prev);
      if (next.has(hotelId)) next.delete(hotelId);
      else next.add(hotelId);
      return next;
    });
  };

  // Drops any pre-filled selected_guest_ids that no longer correspond to a real
  // ParticipantGuest (deleted via the GUEST tab since this booking was made) —
  // otherwise a stale id stays checked-but-invisible and 400s the whole save.
  useEffect(() => {
    if (!guests.length) return;
    setSelectedGuestIds((prev) => prev.filter((id) => guests.some((g) => g._id === id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guests]);

  const toggleGuest = (id) => {
    setSelectedGuestIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleFromChange = (value) => {
    setFromKey(value);
    if (toKey && value >= toKey) {
      const next = addDaysToLADateKey(value, 1);
      setToKey(stayOutKey && next > stayOutKey ? stayOutKey : next);
    }
  };

  const handleUnitSelect = (hotel, roomType, unit) => {
    setSelectedUnit({
      room_unit_id: unit.room_unit_id,
      room_label: unit.room_label,
      beds_free: unit.beds_free,
      hotel_id: hotel.hotel_id,
      hotel_name: hotel.hotel_name,
      room_type: roomType.room_type,
      price: unit.price,
      config_name: unit.config_name,
    });
    setPrice(String(unit.price ?? ''));
    setSubmitError('');
  };

  const postNights = async (keysToPost) => {
    const validGuestIds = selectedGuestIds.filter((id) => guests.some((g) => g._id === id));
    const failures = [];
    for (const dateKey of keysToPost) {
      try {
        await API.post('/ems/room-bookings', {
          room_unit_id: selectedUnit.room_unit_id,
          date: dateKey,
          participant_id: participantId,
          guest_count: validGuestIds.length,
          is_guest_only: isGuestOnly,
          selected_guest_ids: validGuestIds,
          allocation_type: allocationType,
          price: price !== '' ? Number(price) : undefined,
          competition_id: competitionId,
          hotel_id: selectedUnit.hotel_id,
        });
      } catch (err) {
        failures.push({ dateKey, message: err.response?.data?.message || 'Failed' });
      }
    }
    return failures;
  };

  const runCreateBooking = async () => {
    setSubmitting(true);
    setSubmitError('');
    const failures = await postNights(pendingDateKeys);
    setSubmitting(false);
    if (failures.length === 0) {
      setFailedNights([]);
      setPendingDateKeys([]);
      onDone();
      return;
    }
    setFailedNights(failures);
    setPendingDateKeys(failures.map((f) => f.dateKey));
  };

  const handleCreateConfirm = () => {
    if (isGuestOnly && selectedGuestIds.length === 0) {
      setSubmitError('At least 1 guest required for guest-only booking');
      return;
    }
    if (!selectedUnit) { setSubmitError('Select a room unit first.'); return; }
    if (!pendingDateKeys.length) { setSubmitError('No nights to book.'); return; }
    if (failedNights.length === 0 && existingSegments.length) {
      const overlap = findOverlap(existingSegments, fromKey, toKey);
      if (overlap) {
        const overlapRange = formatLADateRangeArrow(overlap.from_date, addDaysToLADateKey(overlap.to_date, 1));
        setOverlapWarning({
          message: `This overlaps with ${overlap.hotel_name} ${overlap.room_type} ${overlap.room_label}, ${overlapRange}.`,
        });
        return;
      }
    }
    runCreateBooking();
  };

  const handleEditConfirm = async () => {
    if (isGuestOnly && selectedGuestIds.length === 0) {
      setSubmitError('At least 1 guest required for guest-only booking');
      return;
    }
    const validGuestIds = selectedGuestIds.filter((id) => guests.some((g) => g._id === id));
    setSubmitting(true);
    setSubmitError('');
    const failures = [];
    for (const id of group.booking_ids) {
      try {
        await API.patch(`/ems/room-bookings/${id}`, {
          allocation_type: allocationType,
          price: price !== '' ? Number(price) : undefined,
          guest_count: validGuestIds.length,
          is_guest_only: isGuestOnly,
          selected_guest_ids: validGuestIds,
        });
      } catch (err) {
        failures.push({ id, message: err.response?.data?.message || 'Failed' });
      }
    }
    setSubmitting(false);
    if (failures.length === 0) {
      onDone();
    } else if (failures.length === group.booking_ids.length) {
      setSubmitError(failures.map((f) => f.message).join('; '));
    } else {
      setSubmitError(
        `Updated ${group.booking_ids.length - failures.length} of ${group.booking_ids.length} nights. Some failed — you can try again.`
      );
    }
  };

  const bookedNightsSoFar = nights - pendingDateKeys.length;
  const total = (Number(price) || 0) * nights;

  return (
    <div className="pbt-panel">
      <div className="pbt-panel__header">
        <span className="pbt-panel__title">{isEdit ? 'Edit Booking' : 'Book a Room'}</span>
        {isEdit ? (
          <span className="pbt-panel__dates">
            {formatLADateRangeArrow(group.from_date, addDaysToLADateKey(group.to_date, 1))}
            &nbsp;&middot;&nbsp;{group.nights} night{group.nights !== 1 ? 's' : ''}
          </span>
        ) : (
          <span className="pbt-panel__dates">
            {nights} night{nights !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {isEdit ? (
        <>
          <div className="pbt-panel__locked-unit">
            <BedDouble size={14} />
            <span>{group.hotel_name} &middot; {group.room_type} &middot; {group.room_label}</span>
            {group.room_id && (
              <Settings
                size={14}
                className="pbt-config-gear"
                title={`Manage configurations for ${group.room_type}`}
                onClick={() => setConfigModalRoom({ _id: group.room_id, room_name: group.room_type })}
              />
            )}
          </div>
          {group.config_name && (
            <div className="pbt-panel__locked-config">
              <b>{group.config_name}</b>
              {totalCapacity(group.beds) > 0 && ` · Sleeps ${totalCapacity(group.beds)}`}
              {[...(group.beds || []).map((b) => `${b.count}× ${b.type}`), ...(group.features || [])].map((part) => ` · ${part}`).join('')}
            </div>
          )}
        </>
      ) : (
        <>
          <BookingDateRangePicker
            fromKey={fromKey}
            toKey={toKey}
            stayInKey={stayInKey}
            stayOutKey={stayOutKey}
            onFromChange={handleFromChange}
            onToChange={setToKey}
          />

          {!validRange ? (
            <p className="pbt-panel__hint">Select a valid date range within the stay period.</p>
          ) : (
            <>
              <p className="pbt-panel__hint">Select an available room unit:</p>

              {loadingAvailability && (
                <div className="pbt-skeleton-hotels" aria-label="Loading availability" aria-busy="true">
                  <div>
                    <div className="pbt-skeleton-hotel__name" />
                    <div className="pbt-skeleton-row">
                      <div className="pbt-skeleton-unit" />
                      <div className="pbt-skeleton-unit" />
                      <div className="pbt-skeleton-unit" />
                    </div>
                  </div>
                </div>
              )}

              {!loadingAvailability && availError && (
                <div className="pbt-panel__error-banner">
                  <AlertTriangle size={14} />
                  <span>{availError}</span>
                  <button type="button" className="btn btn-sm pbt-btn-retry" onClick={fetchAvailability}>
                    <RefreshCw size={12} /> Retry
                  </button>
                </div>
              )}

              {!loadingAvailability && !availError && (
                <HotelAvailabilityList
                  availability={availability}
                  selectedUnit={selectedUnit}
                  collapsedHotels={collapsedHotels}
                  onToggleHotel={toggleHotelCollapsed}
                  onSelectUnit={handleUnitSelect}
                  onOpenConfig={setConfigModalRoom}
                />
              )}
            </>
          )}
        </>
      )}

      {(isEdit || selectedUnit) && (
        <div className="pbt-panel__details">
          {!isEdit && selectedUnit && (
            <div className="pbt-panel__locked-unit">
              <BedDouble size={14} />
              <span>{selectedUnit.hotel_name} &middot; {selectedUnit.room_type} &middot; {selectedUnit.room_label}</span>
            </div>
          )}

          <GuestPicker
            guests={guests}
            selectedGuestIds={selectedGuestIds}
            isGuestOnly={isGuestOnly}
            onToggleGuest={toggleGuest}
            onToggleGuestOnly={(e) => setIsGuestOnly(e.target.checked)}
          />

          <div className="pbt-panel__fields-row">
            <div className="pbt-panel__field">
              <label className="pbt-label">Allocation type</label>
              <select
                className="form-select form-select-sm pbt-select"
                value={allocationType}
                onChange={(e) => setAllocationType(e.target.value)}
              >
                {ALLOCATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="pbt-panel__field">
              <label className="pbt-label">No. of guests</label>
              <input
                type="number"
                className="form-control form-control-sm pbt-price-input"
                value={selectedGuestIds.length}
                readOnly
                title="Determined by selected guests above"
              />
            </div>
            <div className="pbt-panel__field">
              <label className="pbt-label">Price / night ($)</label>
              <input
                type="number"
                className="form-control form-control-sm pbt-price-input"
                value={price}
                min={0}
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
          </div>
          {!isEdit && (
            <div className="pbt-panel__total-row">
              <span className="pbt-panel__total-label">Total for {nights} night{nights !== 1 ? 's' : ''}</span>
              <span className="pbt-panel__total-amount">${total.toLocaleString()}</span>
            </div>
          )}
        </div>
      )}

      {!isEdit && (
        <BookingFailureBanner
          failedNights={failedNights}
          nights={nights}
          bookedNightsSoFar={bookedNightsSoFar}
        />
      )}

      {submitError && <div className="pbt-panel__error">{submitError}</div>}

      {overlapWarning && (
        <ConfirmModal
          title="Overlapping dates"
          message={`${overlapWarning.message} Continue booking anyway?`}
          confirmLabel="Continue"
          cancelLabel="Go Back"
          variant="warning"
          onConfirm={() => { setOverlapWarning(null); runCreateBooking(); }}
          onClose={() => setOverlapWarning(null)}
        />
      )}

      <div className="pbt-panel__actions">
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onDone}>
          Close
        </button>
        {isEdit ? (
          <button type="button" className="btn btn-sm pbt-btn-confirm" onClick={handleEditConfirm} disabled={submitting}>
            {submitting ? 'Saving…' : 'Save Changes'}
          </button>
        ) : (
          <button type="button" className="btn btn-sm pbt-btn-confirm" onClick={handleCreateConfirm} disabled={submitting || !validRange}>
            {submitting
              ? 'Saving…'
              : failedNights.length > 0
                ? `Retry Failed Nights (${pendingDateKeys.length})`
                : 'Confirm Booking'}
          </button>
        )}
      </div>

      {configModalRoom && (
        <ConfigurationList
          room={configModalRoom}
          onClose={() => {
            setConfigModalRoom(null);
            if (!isEdit) fetchAvailability();
          }}
        />
      )}
    </div>
  );
};

export default ParticipantBookingPanel;
